import { Button, TextTooltip } from '@components'
import { getBankIntegrationPageURL } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaQuestionCircle } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { WHEN_NARROW, WHEN_STACKED } from '../../layout'
import { CardSurface } from '../shared/Card'

const GHOST_PATH = 'M 0 62 Q 60 58 120 52 T 250 40 T 400 26'

export const CashEmptyState: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)

  return (
    <FadeIn
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <EmptySurface>
        <TopRow>
          <HeroLabel>{t('dashboard.cash.title')}</HeroLabel>
          <TextTooltip tooltip={t('dashboard.cash.tooltip')} placement="bottom">
            <HeroHelpIcon />
          </TextTooltip>
          <Spacer />
          <StatusPill>
            <StatusDot />
            {t('dashboard.cash.empty.statusPill')}
          </StatusPill>
        </TopRow>

        <AmountPlaceholder>{t('dashboard.cash.empty.amountPlaceholder')}</AmountPlaceholder>
        <Subline>{t('dashboard.cash.empty.subline')}</Subline>

        <GhostChart aria-hidden="true">
          <GhostPlot>
            <svg viewBox="0 0 400 80" preserveAspectRatio="none" role="presentation">
              <line
                x1="0"
                y1="20"
                x2="400"
                y2="20"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1="0"
                y1="45"
                x2="400"
                y2="45"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1="0"
                y1="70"
                x2="400"
                y2="70"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={GHOST_PATH}
                fill="none"
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="2.5"
                strokeDasharray="4 6"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </GhostPlot>
        </GhostChart>

        <CtaRow>
          <CtaCopy>
            <CtaTitle>{t('dashboard.cash.empty.title')}</CtaTitle>
            <CtaDescription>
              {t(
                canManageIntegrations
                  ? 'dashboard.cash.empty.description'
                  : 'dashboard.cash.empty.noPermission'
              )}
            </CtaDescription>
          </CtaCopy>
          {canManageIntegrations && (
            <CtaLink to={getBankIntegrationPageURL(slug, 'aspsps')}>
              <CtaButton intent="primary" size="md" icon={<FaPlus />}>
                {t('settings.integrations.bank.createButton')}
              </CtaButton>
            </CtaLink>
          )}
        </CtaRow>
      </EmptySurface>
    </FadeIn>
  )
}

const FadeIn = styled(motion.div)`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;

  ${WHEN_STACKED} {
    height: auto;
  }
`

const EmptySurface = styled(CardSurface)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.menuBg};
  color: ${({ theme }) => theme.colors.neutralWhite};

  ${WHEN_NARROW} {
    padding: ${({ theme }) => theme.spacing.md}rem;
  }

  ${WHEN_STACKED} {
    height: auto;
  }
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const HeroLabel = styled.span`
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  white-space: nowrap;
`

const HeroHelpIcon = styled(FaQuestionCircle)`
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  cursor: help;
`

const Spacer = styled.div.attrs({ ['aria-hidden']: true })`
  flex: 1;
`

const StatusPill = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
  white-space: nowrap;

  ${WHEN_NARROW} {
    display: none;
  }
`

const StatusDot = styled.span`
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.dashboard.onNavyAxis};
`

const AmountPlaceholder = styled.span`
  min-height: 2.25rem;
  font-size: 2.125rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.35);

  ${WHEN_NARROW} {
    font-size: 1.9375rem;
  }
`

const Subline = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
`

const GhostChart = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 1;
  min-height: 0;
  cursor: default;

  ${WHEN_STACKED} {
    flex: 0 0 5.5rem;
    max-height: 7.5rem;
  }
`

const GhostPlot = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 4.625rem;

  ${WHEN_STACKED} {
    flex: 0 0 5.5rem;
    max-height: 7.5rem;
  }

  svg {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
  }
`

const CtaRow = styled.div`
  flex: 0 0 auto;
  margin-top: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

const CtaCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  flex: 1;
  min-width: 12rem;
`

const CtaTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralWhite};
`

const CtaDescription = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
  text-wrap: pretty;
`

const CtaLink = styled(Link)`
  flex: 0 0 auto;
  text-decoration: none;

  ${WHEN_NARROW} {
    width: 100%;
  }
`

const CtaButton = styled(Button)`
  ${WHEN_NARROW} {
    width: 100%;
  }
`
